import {Component, computed, effect, inject, signal, input, output} from '@angular/core';
import {Card} from 'primeng/card';
import {CardTitleComponent} from '../../../../../../../shared/components/header/card-title/card-title.component';
import {TranslocoPipe} from '@jsverse/transloco';
import {LoadingComponent} from '../../../../../../../shared/components/loading/loading.component';
import {
  HrToolProviderCandidateResponse,
  ProviderTenantMappingResponse,
  ProjectDecisionControllerService,
  CompletePhase1RequestParams,
  UpsertProjectDecisionRequestParams,
  ProjectControllerService,
  ProjectChapterType,
  ProjectStepType,
} from '@frontend/shared';
import {rxResource} from '@angular/core/rxjs-interop';
import {ProviderSelectionWinnerComponent} from './components/provider-selection-winner/provider-selection-winner.component';
import {DecisionReasonFieldComponent} from './components/decision-reason-field/decision-reason-field.component';
import {WinnerSelectionComponent} from './components/winner-selection/winner-selection.component';
import {Button} from 'primeng/button';
import {Tooltip} from 'primeng/tooltip';
import {AppPanelComponent} from '../../../../../../../shared/components/header/panel/app-panel.component';
import {firstValueFrom} from 'rxjs';
import {ToastService} from '@frontend/toast';
import {TabStatus} from '../../../../../../../shared/tabs/tab-status.model';
import {ProjectDecisionReasonVM} from './components/provider-selection-winner/provider-selection-winner.types';
import {TAB_STATUS_COMPLETED, TAB_STATUS_IN_PROGRESS} from './step-9-2-2-project-decision.types';
import {toLocalDateString} from '../../../../../../../shared/utils/date-utils';
import {ProjectStepStateService} from '../../../../project-step-state.service';
import {ProjectDataService} from '../../../../../../../shared/services/project-data.service';
import {ConfirmationDialogService} from '../../../../../../../shared/components/dialog/confirmation/confirmation-dialog.service';

@Component({
  selector: 'app-step-9-2-2-project-decision',
  imports: [
    Card,
    TranslocoPipe,
    LoadingComponent,
    ProviderSelectionWinnerComponent,
    DecisionReasonFieldComponent,
    WinnerSelectionComponent,
    Button,
    Tooltip,
    AppPanelComponent,
    CardTitleComponent,
  ],
  templateUrl: './step-9-2-2-project-decision.component.html',
})
export class Step922ProjectDecisionComponent {
  private readonly projectDecisionController = inject(ProjectDecisionControllerService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);
  private readonly projectController = inject(ProjectControllerService);
  private readonly toast = inject(ToastService);
  private readonly stepState = inject(ProjectStepStateService);
  protected readonly projectDataService = inject(ProjectDataService);

  projectId = input.required<number>();
  isCurrentStepLocked = input.required<boolean>();
  updateState = output<TabStatus>();

  protected readonly isDecisionFormComplete = signal<boolean>(false);
  protected readonly unfilledReasons = signal<Set<string>>(new Set());

  readonly winnerCandidatesResource = rxResource({
    params: () => ({projectId: this.projectId()}),
    stream: ({params: request}) => {
      return this.projectDecisionController.getWinnerCandidates({projectId: request.projectId});
    },
    defaultValue: undefined,
  });

  readonly hrToolProviderDecisionResource = rxResource({
    params: () => {
      if (this.winnerCandidatesResource.value()?.winnerSelected !== true) {
        return undefined;
      }
      return {projectId: this.projectId()};
    },
    stream: () => {
      return this.projectDecisionController.getTotalFeesForDecision({projectId: this.projectId()});
    },
    defaultValue: undefined,
  });

  protected readonly winnerSelected = computed(
    () => this.winnerCandidatesResource.value()?.winnerSelected === true,
  );

  protected readonly winner = computed(() => this.hrToolProviderDecisionResource.value()?.winner);
  protected readonly loosers = computed(
    () => this.hrToolProviderDecisionResource.value()?.loosers ?? [],
  );
  protected readonly contractProjectDecision = computed(
    () => this.hrToolProviderDecisionResource.value()?.contractProjectDecision,
  );
  protected readonly contractComment = computed(() => this.contractProjectDecision()?.comment);
  protected readonly isWinnerReasonReadOnly = computed(
    () => this.isCurrentStepLocked() || !!this.contractComment(),
  );

  protected readonly providerOptions = computed<ProviderTenantMappingResponse[]>(() => {
    const winner = this.winner();
    if (winner) {
      return [winner.providerTenantMappingResponse];
    }
    return this.winnerCandidatesResource.value()?.candidates ?? [];
  });

  protected readonly isProviderSelectionReadOnly = computed(
    () => this.isCurrentStepLocked() || this.winnerSelected(),
  );

  protected readonly preselectedProviderId = computed(
    () => this.winner()?.providerTenantMappingResponse.hrToolDirectusId ?? null,
  );

  protected readonly canTagWinnerProvider = computed(
    () =>
      this.isDecisionFormComplete() &&
      this.unfilledReasons().size === 0 &&
      !!this.contractProjectDecision() &&
      !this.isCurrentStepLocked(),
  );

  protected readonly rfcPhaseCompleted = computed(() =>
    this.stepState.areStepsCompleted([
      {
        chapterId: ProjectChapterType.OVERALL_RESULT,
        stepId: ProjectStepType.OVERALL_RESULTS_DECISION,
      },
    ]),
  );

  protected readonly isProjectArchived = computed(
    () => !!this.projectDataService.getProject()()?.archivedAt,
  );

  protected readonly canArchiveProject = computed(
    () =>
      this.contractProjectDecision()?.providerDeclined === false &&
      this.rfcPhaseCompleted() &&
      !this.isProjectArchived(),
  );

  protected readonly contractFormValues = computed<ProjectDecisionReasonVM | null>(() => {
    const contractDecision = this.contractProjectDecision();
    if (!contractDecision) {
      return null;
    }
    return {
      ...contractDecision.hrToolTotalFees,
      contractDurationMonths: contractDecision.contractDurationMonths,
      contractSigningDate: new Date(contractDecision.contractSigningDate),
      comment: contractDecision.comment ?? '',
    };
  });

  protected readonly decisionTabStatus = computed<TabStatus | null>(() => {
    const hasData = this.winnerSelected() || !!this.contractProjectDecision();
    if (!hasData) {
      return null;
    }
    return this.canArchiveProject() ? TAB_STATUS_COMPLETED : TAB_STATUS_IN_PROGRESS;
  });

  constructor() {
    effect(() => {
      const status = this.decisionTabStatus();
      if (status) {
        this.updateState.emit(status);
      }
    });
  }

  protected onReasonSatisfied(hrToolDirectusId: string, satisfied: boolean) {
    this.unfilledReasons.update((current) => {
      const next = new Set(current);
      if (satisfied) {
        next.delete(hrToolDirectusId);
      } else {
        next.add(hrToolDirectusId);
      }
      return next;
    });
  }

  protected onWinnerTagged(response: HrToolProviderCandidateResponse) {
    this.winnerCandidatesResource.set(response);
    this.updateState.emit(TAB_STATUS_IN_PROGRESS);
  }

  protected saveContractDecision(decision: ProjectDecisionReasonVM) {
    const contractDecision = this.contractProjectDecision()!;
    const request: UpsertProjectDecisionRequestParams = {
      projectId: this.projectId(),
      projectDecisionUpsertRequest: {
        projectDecisionId: contractDecision.projectDecisionId,
        hrToolTotalFees: {
          totalServiceFees: decision.totalServiceFees,
          implementationFees: decision.implementationFees,
          integrationSetupFees: decision.integrationSetupFees,
          integrationOtherFees: decision.integrationOtherFees,
          totalAllYearsFees: decision.totalAllYearsFees,
        },
        contractDurationMonths: decision.contractDurationMonths,
        contractSigningDate: toLocalDateString(decision.contractSigningDate),
      },
    };

    this.projectDecisionController.upsertProjectDecision(request).subscribe({
      next: () => {
        void this.toast.showSuccessMessage();
        this.updateState.emit(TAB_STATUS_IN_PROGRESS);
        this.hrToolProviderDecisionResource.reload();
      },
    });
  }

  protected async tagWinnerProvider() {
    const contractDecision = this.contractProjectDecision()!; // accept no NULL here!
    const request: CompletePhase1RequestParams = {
      projectId: this.projectId(),
      projectDecisionId: contractDecision.projectDecisionId,
    };
    const completePhaseAndRefreshSteps = async () => {
      try {
        await firstValueFrom(this.projectDecisionController.completePhase1(request));
        this.updateState.emit(TAB_STATUS_IN_PROGRESS);
      } catch (err) {
        console.error('Complete phase failed:', err);
      }
    };

    this.confirmDialogService.confirm({
      headerKey: `project.tag.winnerProvider.header`,
      messageKey: `project.tag.winnerProvider.message`,
      accept: completePhaseAndRefreshSteps,
    });
  }

  protected async archiveProject() {
    this.projectController.archiveProject({id: this.projectId()}).subscribe({
      next: (project) => {
        this.projectDataService.setProject(project);
        this.toast.showSuccess(
          'project.card.archive.success.header',
          'project.card.archive.success.detail',
        );
      },
    });
  }
}
